import {
  Button,
  ButtonGroup,
  type ButtonProps,
  Modal as CModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  type ModalProps,
} from "@chakra-ui/react";
import type React from "react";
import type { ReactNode } from "react";

import { Heading, Text } from "@equidefi/ui";

export type ConfirmModalProps = Omit<ModalProps, "onClose" | "children"> & {
  title: string;
  isOpen: boolean;
  children?: ReactNode;
  description?: string;
  cancelText?: string;
  confirmText?: string;
  confirmButtonColor?: string;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  showConfirmButton?: boolean;
  contentProps?: ModalContentProps;
  onConfirm?: () => void;
  onCancel: () => void;
  width?: string;
};

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  isOpen = false,
  children,
  description,
  cancelText = "Dismiss",
  confirmText = "Ok",
  confirmButtonColor,
  confirmButtonProps = {},
  cancelButtonProps = {},
  showConfirmButton = true,
  onConfirm,
  onCancel,
  contentProps = {},
  width = "auto",
  ...rest
}) => {
  return (
    <CModal
      isOpen={isOpen}
      onClose={onCancel}
      aria-labelledby="contained-modal-title-vcenter"
      motionPreset="slideInBottom"
      isCentered
      {...rest}>
      <ModalOverlay backdropFilter="blur(8px)" />

      <ModalContent
        p={5}
        maxW={{ base: "full", md: "none" }}
        w={{ base: "full", md: width }}
        minW={{ base: "auto", md: "480px" }}
        mx={{ base: "15px", md: "auto" }}
        borderRadius="13px"
        {...contentProps}>
        <ModalHeader p={0}>
          <Heading textStyle="h2" fontWeight="bold" fontSize="20px" color="black">
            {title}
          </Heading>
          <ModalCloseButton />
        </ModalHeader>

        <ModalBody p={0} mb={0}>
          {description && (
            <Text textStyle="body" mb="8">
              {description}
            </Text>
          )}
          {children}
        </ModalBody>

        <ModalFooter p={0} justifyContent="start">
          <ButtonGroup spacing={4} size="md">
            {showConfirmButton && (
              <Button
                onClick={onConfirm}
                aria-label={confirmText}
                colorScheme={confirmButtonColor ?? "purple"}
                variant="solid"
                transition=".1s"
                fontWeight="semibold"
                fontSize="md"
                {...confirmButtonProps}>
                {confirmText}
              </Button>
            )}
            <Button
              onClick={onCancel}
              aria-label={cancelText}
              colorScheme="primary"
              variant="outline"
              bg="transparent"
              transition=".1s"
              fontWeight="semibold"
              fontSize="md"
              {...cancelButtonProps}>
              {cancelText}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </CModal>
  );
};
